import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const DailyModules = () => {
  const [modules, setModules] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    subject: '',
    topicsTaught: '',
    description: '',
    nextDayTopic: '',
    nextDayDescription: '',
    file: null
  });

  useEffect(() => {
    fetchClassInfo();
    fetchModules();
  }, []);

  const fetchClassInfo = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes/my-class`);
      setClassInfo(res.data);
    } catch (err) {
      console.error('Error fetching class:', err);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/daily-modules`);
      setModules(res.data);
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('date', form.date);
      formData.append('classId', classInfo._id);
      formData.append('subject', form.subject);
      formData.append('topicsTaught', form.topicsTaught);
      formData.append('description', form.description);
      formData.append('nextDayTopic', form.nextDayTopic);
      formData.append('nextDayDescription', form.nextDayDescription);
      if (form.file) formData.append('file', form.file);

      if (editId) {
        await axios.put(`${API_BASE}/daily-modules/${editId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE}/daily-modules`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      resetForm();
      fetchModules();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving module');
    }
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      subject: '',
      topicsTaught: '',
      description: '',
      nextDayTopic: '',
      nextDayDescription: '',
      file: null
    });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (mod) => {
    setForm({
      date: new Date(mod.date).toISOString().split('T')[0],
      subject: mod.subject,
      topicsTaught: mod.topicsTaught,
      description: mod.description || '',
      nextDayTopic: mod.nextDaySchedule?.topic || '',
      nextDayDescription: mod.nextDaySchedule?.description || '',
      file: null
    });
    setEditId(mod._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this module?')) return;
    try {
      await axios.delete(`${API_BASE}/daily-modules/${id}`);
      fetchModules();
    } catch (err) {
      alert('Error deleting module');
    }
  };

  const [pdfUrl, setPdfUrl] = useState(null);

  const viewPdf = (id) => {
    const token = localStorage.getItem('token');
    setPdfUrl(`${API_BASE}/daily-modules/download/${id}?token=${token}`);
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex justify-between items-center px-6 py-3">
            <h1 className="text-xl font-semibold text-gray-800">Daily Modules</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Daily Teaching Log</h2>
              {classInfo && (
                <p className="text-gray-600">
                  Class: {classInfo.className} - {classInfo.section}
                </p>
              )}
            </div>
            <button
              onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
            >
              {showForm ? 'Cancel' : '+ Upload Module'}
            </button>
          </div>

          {/* Form */}
          {showForm && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold mb-4">
                {editId ? 'Edit Module' : 'Upload Today\'s Module'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="e.g. Mathematics"
                      className="w-full border rounded-lg px-3 py-2"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Topics Taught Today</label>
                  <textarea
                    value={form.topicsTaught}
                    onChange={(e) => setForm({ ...form, topicsTaught: e.target.value })}
                    placeholder="What topics were covered today?"
                    className="w-full border rounded-lg px-3 py-2"
                    rows="2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Additional notes for the day"
                    className="w-full border rounded-lg px-3 py-2"
                    rows="2"
                  />
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-2">📅 Next Day Schedule</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Topic for Next Day</label>
                      <input
                        type="text"
                        value={form.nextDayTopic}
                        onChange={(e) => setForm({ ...form, nextDayTopic: e.target.value })}
                        placeholder="What's planned for tomorrow?"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Next Day Description</label>
                      <input
                        type="text"
                        value={form.nextDayDescription}
                        onChange={(e) => setForm({ ...form, nextDayDescription: e.target.value })}
                        placeholder="Homework, things to prepare, etc."
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Upload PDF (Optional)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
                  >
                    {editId ? 'Update Module' : 'Upload Module'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Modules List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : modules.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No modules uploaded yet. Click "Upload Module" to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {modules.map((mod) => (
                <div key={mod._id} className="bg-white rounded-lg shadow p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                          {mod.subject}
                        </span>
                        <span className="text-gray-500 text-sm">{formatDate(mod.date)}</span>
                        {mod.classId && (
                          <span className="text-gray-400 text-sm">
                            {mod.classId.className} - {mod.classId.section}
                          </span>
                        )}
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">Topics: {mod.topicsTaught}</h4>
                      {mod.description && (
                        <p className="text-gray-600 text-sm mb-2">{mod.description}</p>
                      )}
                      {mod.fileName && (
                        <p className="text-gray-400 text-xs mb-1">📎 {mod.fileName}</p>
                      )}
                      {mod.nextDaySchedule?.topic && (
                        <div className="bg-blue-50 rounded p-3 mt-2">
                          <p className="text-sm font-medium text-blue-800">📅 Next Day: {mod.nextDaySchedule.topic}</p>
                          {mod.nextDaySchedule.description && (
                            <p className="text-sm text-blue-600">{mod.nextDaySchedule.description}</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {mod.filePath && (
                        <button
                          onClick={() => viewPdf(mod._id)}
                          className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200"
                        >
                          📄 View PDF
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(mod)}
                        className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded text-sm hover:bg-yellow-200"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(mod._id)}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl h-5/6 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">PDF Viewer</h3>
              <div className="flex gap-2">
                <a
                  href={pdfUrl}
                  download
                  className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded text-sm hover:bg-indigo-200"
                >
                  ⬇ Download
                </a>
                <button
                  onClick={() => setPdfUrl(null)}
                  className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="flex-1 p-2">
              <iframe
                src={pdfUrl}
                title="PDF Viewer"
                className="w-full h-full rounded border"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { DailyModules as default };
